import { Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import type { DetalheDoConvenio as Detalhe } from "@/api/contrato";
import { useBancos, useBloquearConvenio, useContas, useConvenio } from "@/api/consultas";
import { recursoInexistente } from "@/api/problema";
import { useAvisos } from "@/componentes/Avisos";
import { BlocoDeCodigo } from "@/componentes/BlocoDeCodigo";
import { Botao } from "@/componentes/Botao";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Dica } from "@/componentes/Dica";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { Interruptor } from "@/componentes/Interruptor";
import { NaoEncontrado } from "@/componentes/NaoEncontrado";
import { PainelLateral } from "@/componentes/PainelLateral";
import { PainelRecolhivel } from "@/componentes/PainelRecolhivel";
import { Picote } from "@/componentes/Picote";
import { Pilula, PilulaDeExcecao } from "@/componentes/PilulaDeSituacao";
import { useAlternarSituacao } from "@/componentes/useAlternarSituacao";
import { useModalNaUrl } from "@/componentes/useModalNaUrl";
import { banco, data } from "@/formato";
import { rotaDoCedente } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { bancoDoCatalogo } from "./bancos";
import { CartaoDoCertificado } from "./CartaoDoCertificado";
import { mostraCartaoDoCertificado } from "./certificado";
import { rotuloDoTipo } from "./conta";
import {
  camposDoBanco,
  comDigito,
  corpoDoDesbloqueio,
  matizDoStatus,
  regrasDoBanco,
  rotuloDoModo,
  rotuloDoStatus,
  situacaoDaJanela,
  usaWebService,
} from "./convenio";
import { EditarConvenio } from "./EditarConvenio";
import { ProvisionarConvenio, SeloDoProvisionamento } from "./ProvisionarConvenio";
import { textoDaRecusa } from "./recusas";
import { TrocarCertificado } from "./TrocarCertificado";

/*
 * O convênio — doc 06 §4.4.
 *
 * "O convênio é a entidade operacional central (doc 01 §3.6)": é ele que
 * carrega banco, carteira, modo de registro, capacidades e o sequencial de
 * nosso número.
 *
 * Rota filha da aba Contas: o id vem do caminho, e fechar é navegar para a aba. Os dois
 * painéis são os da GUI da TecnoSpeed (design D4), com todos os campos: um dígito
 * diferente do provider é recusa da emissão, e o operador confere campo a campo.
 *
 * Só o que o banco usa aparece, e dado gravado sempre aparece. Sem o catálogo, sobram os
 * campos preenchidos: a tela perde a dica de um campo vazio, e não inventa nenhum.
 */
export function DetalheDoConvenio() {
  const { id: pessoaID = "", convenioId = "" } = useParams();
  const navegar = useNavigate();
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();
  const modal = useModalNaUrl();

  const { data: detalhe, error } = useConvenio(cliente, convenioId);
  const { data: lista } = useContas(cliente, pessoaID);
  const bloquear = useBloquearConvenio(cliente, convenioId);

  const ehAdmin = pode("admin");
  const contas = rotaDoCedente(pessoaID, "contas");

  /*
   * A leitura administrativa atravessa os cedentes, então o `200` de um convênio de outro
   * cedente não diz que ele é deste: o caminho é que mente, e a tela responde como a um
   * identificador que não leva a nada, sem mostrar nada do convênio alheio.
   */
  const inexistente =
    recursoInexistente(error) ||
    (detalhe !== undefined && detalhe.convenio.pessoa_id !== pessoaID);
  const lido = inexistente ? undefined : detalhe;
  const convenio = lido?.convenio;

  const bloqueado = convenio?.status === "bloqueado";
  const nome = convenio === undefined ? "" : `${convenio.banco}/${convenio.numero}`;
  const provisionado = convenio?.provisionamento.estado === "provisionado";

  // A situação da conta vem da lista da aba, atrás do painel: a leitura do convênio não a traz.
  const contaInativa =
    lista?.contas.find((conta) => conta.conta_id === convenio?.conta_id)?.status === "inativa";

  const situacao = useAlternarSituacao({
    ligado: !bloqueado,
    desligar: {
      titulo: `Bloquear o convênio ${nome}?`,
      corpo: "Recusa emissão nova por este convênio; os boletos emitidos não mudam.",
      verbo: "Bloquear convênio",
    },
    ligar: {
      titulo: `Desbloquear o convênio ${nome}?`,
      corpo: corpoDoDesbloqueio(contaInativa),
      verbo: "Desbloquear convênio",
    },
    aoAlterar: async (ligar, motivo) => {
      try {
        await bloquear.mutateAsync({
          bloqueado: !ligar,
          ...(motivo !== "" ? { motivo } : {}),
        });
      } catch (falha) {
        // A confirmação mostra a mensagem do erro, e o `detail` não é texto de tela.
        throw new Error(textoDaRecusa(falha), { cause: falha });
      }
      avisos.feito(ligar ? "Convênio desbloqueado" : "Convênio bloqueado");
    },
  });

  function aoFechar() {
    void navegar(contas);
  }

  /*
   * Um `PainelLateral` só, do carregando ao pronto: trocar a instância repetiria a
   * animação de entrada, e o foco cairia no `body` no meio da leitura.
   *
   * `EditarConvenio` mora DENTRO do painel. No link direto, os dois nasceriam no mesmo
   * commit, e o `hideOthers` do painel marcaria o modal como inerte para leitor de tela.
   * Aninhado, ele só existe depois do conteúdo do painel, como o Radix prevê.
   */
  return (
    <>
      <PainelLateral
        aberto
        aoFechar={aoFechar}
        descricao={
          convenio === undefined ? "Detalhe do convênio" : `Detalhe do convênio ${convenio.numero}`
        }
        titulo={
          convenio === undefined ? (
            <>
              <span className="sr-only">Convênio</span>
              {!inexistente && error === null && <Esqueleto className="h-5 w-40" />}
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="dados truncate text-titulo font-semibold text-texto">{nome}</span>
                {(!contaInativa || convenio.status !== "ativo") && (
                  <PilulaDeExcecao
                    normal={convenio.status === "ativo"}
                    matiz={matizDoStatus(convenio.status)}
                  >
                    {rotuloDoStatus(convenio.status)}
                  </PilulaDeExcecao>
                )}
                {contaInativa && <Pilula matiz="encerrado">conta inativa</Pilula>}
                <SeloDoProvisionamento provisionamento={convenio.provisionamento} />
              </div>
              <span className="truncate text-[12px] text-texto-2">
                {convenio.pessoa_nome ?? "—"}
              </span>
            </div>
          )
        }
        rodape={
          convenio !== undefined && ehAdmin ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="rotulo">Situação</span>
                <Interruptor
                  ligado={!bloqueado}
                  apagado={contaInativa}
                  rotulo={contaInativa ? "Convênio sem bloqueio, conta inativa" : "Convênio ativo"}
                  aoAlternar={situacao.pedir}
                />
              </div>
              <div className="flex items-center gap-2">
                {!provisionado && (
                  <Botao onClick={() => modal.abrirModal({ abrir: "provisionar" })}>
                    Provisionar
                  </Botao>
                )}
                <Botao tom="secundario" onClick={() => modal.abrirModal({ abrir: "editar" })}>
                  <Pencil aria-hidden="true" className="size-4" />
                  Editar
                </Botao>
              </div>
            </div>
          ) : undefined
        }
      >
        {inexistente && (
          <div className="p-4">
            <NaoEncontrado texto="Convênio não encontrado." para={contas} rotuloDoLink="Contas" />
          </div>
        )}

        {!inexistente && lido === undefined && (
          <div aria-busy={error === null} className="flex flex-col gap-4 p-4">
            {error === null ? (
              <>
                <Esqueleto className="h-[360px]" />
                <Esqueleto className="h-[280px]" />
              </>
            ) : (
              <FaixaDeErro erro={error} />
            )}
          </div>
        )}

        {lido !== undefined && <ConteudoDoConvenio detalhe={lido} erro={error} />}

        {modal.abrir === "editar" && ehAdmin && convenio !== undefined && (
          <EditarConvenio convenio={convenio} aoFechar={modal.fechar} />
        )}

        {/* O envio depende do cadastro lá: sem ele, a API recusaria o arquivo já escolhido. */}
        {modal.abrir === "certificado" && ehAdmin && convenio !== undefined && provisionado && (
          <TrocarCertificado
            alvo={{ nivel: "convenio", id: convenio.id }}
            nome={nome}
            troca={convenio.certificado.situacao !== "ausente"}
            aoFechar={modal.fechar}
          />
        )}

        {modal.abrir === "provisionar" && ehAdmin && convenio !== undefined && !provisionado && (
          <ProvisionarConvenio convenio={convenio} aoFechar={modal.fechar} />
        )}
      </PainelLateral>

      {situacao.dialogo}
    </>
  );
}

function simOuNao(valor: boolean | undefined): string | null {
  if (valor === undefined) return null;
  return valor ? "Sim" : "Não";
}

/** Objeto ou lista vazia é "nenhuma declarada", e o bloco não aparece (spec). */
function temCapacidades(capacidades: unknown): boolean {
  if (capacidades === null || capacidades === undefined || capacidades === "") return false;
  if (typeof capacidades !== "object") return true;
  return Object.keys(capacidades).length > 0;
}

function ConteudoDoConvenio({
  detalhe,
  erro,
}: {
  detalhe: Detalhe;
  /** A releitura que falhou: o dado anterior fica na tela, com a faixa. */
  erro: unknown;
}) {
  const { convenio } = detalhe;
  const { cliente } = useSessao();
  const catalogo = useBancos(cliente, true);

  const linha = bancoDoCatalogo(catalogo.data?.bancos ?? [], convenio.banco);
  const regras = regrasDoBanco(linha);
  const opcionais = camposDoBanco(regras, convenio);
  const janela = situacaoDaJanela(convenio);
  const { certificado, pessoa_id: pessoaID, provisionamento } = convenio;

  return (
    <div className="flex flex-col gap-4 p-4">
      {erro !== null && <FaixaDeErro erro={erro} />}

      <PainelRecolhivel titulo="Dados">
        <div className="grid grid-cols-2 gap-2">
          <CaixaDeCampo rotulo="Banco">{banco(convenio.banco)}</CaixaDeCampo>
          <CaixaDeCampo rotulo="Tipo de conta">{rotuloDoTipo(convenio.conta_tipo)}</CaixaDeCampo>
          <CaixaDeCampo rotulo="Agência" dados>
            {comDigito(convenio.agencia, convenio.agencia_dv)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Conta" dados>
            {comDigito(convenio.conta, convenio.conta_dv)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Código do beneficiário" dados>
            {convenio.codigo_beneficiario || null}
          </CaixaDeCampo>
          {convenio.banco_correspondente ? (
            <CaixaDeCampo rotulo="Banco correspondente" dados>
              {convenio.banco_correspondente}
            </CaixaDeCampo>
          ) : null}
          <CaixaDeCampo rotulo="Número do convênio" dados>
            {convenio.numero}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Carteira" dados>
            {convenio.carteira}
            {convenio.variacao ? `/${convenio.variacao}` : ""}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Padrão CNAB" dados>
            {convenio.padrao_cnab || null}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Alteração bancária">
            <span className="flex items-center gap-1">
              {janela.texto}
              {janela.dica !== undefined && <Dica texto={janela.dica} />}
            </span>
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Descrição" className="col-span-2">
            {convenio.descricao || null}
          </CaixaDeCampo>
        </div>
      </PainelRecolhivel>

      <PainelRecolhivel titulo="Configurações">
        <div className="grid grid-cols-2 gap-2">
          <CaixaDeCampo rotulo="Modo de registro">
            {rotuloDoModo(convenio.modo_registro)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Registro instantâneo">
            {simOuNao(convenio.registro_instantaneo)}
          </CaixaDeCampo>
          {usaWebService(convenio.modo_registro) || convenio.tipo_webservice ? (
            <CaixaDeCampo rotulo="Tipo de WebService" dados>
              {convenio.tipo_webservice || null}
            </CaixaDeCampo>
          ) : null}
          {opcionais.contrato ? (
            <CaixaDeCampo rotulo="Número do contrato" dados>
              {convenio.numero_contrato || null}
            </CaixaDeCampo>
          ) : null}
          {opcionais.remessa ? (
            <CaixaDeCampo rotulo="Número da remessa" dados>
              {convenio.numero_remessa || null}
            </CaixaDeCampo>
          ) : null}
          <CaixaDeCampo rotulo="Reiniciar remessa diariamente">
            {simOuNao(convenio.reiniciar_remessa_diariamente)}
          </CaixaDeCampo>
          {opcionais.byte ? (
            <CaixaDeCampo rotulo="Byte do nosso número" dados>
              {convenio.byte_nosso_numero || null}
            </CaixaDeCampo>
          ) : null}
          <CaixaDeCampo rotulo="Provider">{convenio.provider || null}</CaixaDeCampo>
          <CaixaDeCampo rotulo="Cadastrado em">
            {convenio.criado_em ? data(convenio.criado_em) : null}
          </CaixaDeCampo>
        </div>
      </PainelRecolhivel>

      {mostraCartaoDoCertificado(linha, convenio) && (
        <CartaoDoCertificado
          certificado={certificado}
          convenioID={convenio.id}
          travado={
            provisionamento.estado === "provisionado"
              ? undefined
              : "Provisione o convênio antes: o certificado depende do cadastro lá."
          }
          trocaEm={
            certificado.nivel === "cedente"
              ? rotaDoCedente(pessoaID, "dados", { abrir: "certificado" })
              : undefined
          }
        />
      )}

      <Picote />

      <div className="grid grid-cols-2 gap-2">
        <CaixaDeCampo rotulo="Próximo nosso número" dados>
          {/*
            O doc 06 §4.4 pede este número na tela: ele é o que denuncia
            sequencial estourado ou fora de faixa ANTES de o banco recusar
            a emissão. Só leitura, sempre: repetir nosso número é o risco
            existencial do produto.
          */}
          <span className="flex items-center gap-1">
            {detalhe.proximo_nosso_numero}
            <Dica texto="Não muda depois de criado: número repetido é título duplicado no banco." />
          </span>
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Liberados aguardando reuso" dados>
          {/*
            Nossos números devolvidos por emissão que falhou. Um número
            alto aqui é sintoma, e não folga.
          */}
          {detalhe.liberados_aguardando}
        </CaixaDeCampo>
      </div>

      <section className="flex flex-col gap-2">
        {/*
          ┌──────────────────────────────────────────────────────────────┐
          │ O VALOR DA CREDENCIAL NUNCA APARECE. NEM MASCARADO.          │
          │                                                              │
          │ Mascarado ainda vaza tamanho e formato — e, pior, transmite  │
          │ ao operador a ideia de que existe um caminho para vê-lo.     │
          │ Não existe: a role do painel sequer alcança a tabela de      │
          │ credencial, ela lê uma view que não expõe o material         │
          │ cifrado.                                                     │
          └──────────────────────────────────────────────────────────────┘
        */}
        <div className="flex items-center gap-1">
          <h3 className="rotulo">Credenciais</h3>
          <Dica texto="O valor não é exibido, nem mascarado: a leitura do painel não o alcança." />
        </div>

        {detalhe.credenciais.length === 0 ? (
          <p className="text-texto-2">Nenhuma credencial.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {detalhe.credenciais.map((credencial) => (
              <CaixaDeCampo key={credencial.tipo} rotulo={credencial.tipo}>
                configurada em {data(credencial.configurada_em)}
              </CaixaDeCampo>
            ))}
          </div>
        )}
      </section>

      {temCapacidades(convenio.capacidades) && (
        <BlocoDeCodigo rotulo="Capacidades" conteudo={convenio.capacidades} />
      )}
    </div>
  );
}
